import { FC } from 'react'
import Modal from '@/UI/Modals/Modal'
import { useTranslation } from 'next-i18next'

import { ModalBody, ModalFrame } from '@/UI/Modals/ModalParts'
import GrainButton from '@/shared/GrainButton'

import { ArrowRightIcon } from '@/components/icons'

type FreeTrialTermsPrimerModalProps = {
  onContinue: () => void
}

/**
 * Sets expectations before the agreement itself is put in front of the
 * customer: the next screen is long, and the checkbox that unlocks the trial
 * sits at the bottom of it.
 *
 * Deliberately has no dismiss affordance — enrolment is refused server-side
 * until the terms are accepted, so backing out here would only lead to a
 * silently ignored deep link.
 */
export const FreeTrialTermsPrimerModal: FC<FreeTrialTermsPrimerModalProps> = ({ onContinue }) => {
  const { t } = useTranslation('common')

  return (
    <ModalFrame
      show
      dialogClassName="free-trial-terms-primer-dialog w-[400px] max-w-full"
      // `[&]:` moves the border to the variant band, so it beats the equally
      // specific `border: none` that every `.modal-content` carries.
      contentClassName="free-trial-terms-content [box-shadow:0px_4px_20px_0px_rgba(0,0,0,0.1)] [&]:[border:1px_solid_var(--gray-5)]"
    >
      <Modal.Header className="justify-between gap-[16px] p-[24px] [&.modal-header]:[border-bottom:1px_solid_var(--gray-5)]">
        <div className="flex flex-col items-start justify-center">
          <h1 className="m-0">{t('free_trial_terms.primer.title')}</h1>
          <span className="caption-text text-gray-3">
            {t('free_trial_terms.primer.subtitle')}
          </span>
        </div>
      </Modal.Header>

      <ModalBody className="[&.modal-body]:[border-top:none]">
        <p className="m-0 text-gray-1">
          {t('free_trial_terms.primer.body')}
        </p>
      </ModalBody>

      <Modal.Footer className="flex flex-col items-stretch gap-0 [&.modal-footer]:p-0">
        {/* The primer has no consent row above it, so no top rule of its own. */}
        <div className="free-trial-terms-action flex flex-col gap-[16px] p-[24px] [border-top:none]">
          <GrainButton
            type="button"
            variant="primary"
            fullWidth
            text={t('free_trial_terms.continue')}
            icon={<ArrowRightIcon className="size-[16px]" />}
            onClick={onContinue}
          />
        </div>
      </Modal.Footer>
    </ModalFrame>
  )
}

export default FreeTrialTermsPrimerModal
